import { motion } from 'framer-motion';
import { 
  Server, Cloud, Globe, Shield, Phone, 
  Users, Award, Target, BarChart 
} from 'lucide-react';
import { BrandDot } from '../components/brand/BrandMotif';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const ictCapabilities = [
  {
    icon: Server,
    title: 'ICT Strategy & Architecture',
    description: 'Designing enterprise-wide technology architectures aligned with business strategy.',
    items: [
      'Digital transformation roadmaps',
      'Enterprise architecture design',
      'Technology vendor evaluation',
      'ICT governance frameworks',
      'IT cost optimisation strategies',
    ],
  },
  {
    icon: Cloud,
    title: 'Cloud & Data Infrastructure',
    description: 'Building secure and scalable cloud and hybrid infrastructure environments.',
    items: [
      'Cloud migration and hybrid architecture',
      'Data centre design and modernisation',
      'Multi-cloud orchestration',
      'Disaster recovery and business continuity',
      'Cloud cost governance and optimisation',
    ],
  },
  {
    icon: Globe,
    title: 'Network & Connectivity',
    description: 'Designing high-performance enterprise networking environments.',
    items: [
      'Enterprise WAN / SD-WAN architecture',
      'Campus and branch networking',
      'Data centre interconnects',
      'Network security architecture',
      'Global connectivity solutions',
    ],
  },
  {
    icon: Shield,
    title: 'Cybersecurity & Risk Management',
    description: 'Protecting enterprise systems through proactive security architecture and governance.',
    items: [
      'Security architecture design',
      'Zero-trust security frameworks',
      'Security monitoring and operations',
      'Compliance and risk assessments',
      'Identity and access management',
    ],
  },
  {
    icon: Phone,
    title: 'Voice & Unified Communications',
    description: 'Transforming enterprise collaboration and communication environments.',
    items: [
      'Enterprise voice transformation',
      'Contact centre modernisation',
      'Unified communications platforms',
      'Collaboration solutions',
      'AI-driven customer engagement',
    ],
  },
];

const leadershipCapabilities = [
  {
    icon: Users,
    title: 'Consultative Selling Excellence',
    description: 'SGA transforms sales teams from transactional sellers into trusted advisors capable of delivering business outcomes.',
    items: [
      'Strategic client engagement',
      'Solution-oriented selling',
      'Customer value creation',
    ],
  },
  {
    icon: Award,
    title: 'First-Time Manager Enablement',
    description: 'SGA equips new managers with the skills needed to transition successfully from individual contributors to effective leaders.',
    items: [
      'Leadership communication',
      'Performance management',
      'Team development',
    ],
  },
  {
    icon: Target,
    title: 'Leadership Development Programs',
    description: 'Designed to develop leaders capable of navigating uncertainty and driving transformation.',
    items: [
      'Strategic thinking',
      'Change leadership',
      'Organizational resilience',
    ],
  },
  {
    icon: BarChart,
    title: 'Outcome-Based Governance',
    description: 'SGA introduces governance frameworks that connect leadership decisions with measurable business outcomes.',
    items: [
      'Performance frameworks',
      'Decision accountability',
      'Results tracking',
    ],
  },
];

export const ProfessionalServicesPage = () => {
  return (
    <div data-testid="professional-services-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('/assets/banners/Professional_services_Banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-lg"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-4"
            >
              Enterprise Technology & Leadership Transformation
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-inter text-base text-white leading-relaxed"
            >
              Designing secure ICT ecosystems while building leadership capability 
              to drive sustainable business outcomes.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Two-Column Split - Visual Representation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Enterprise ICT Block */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#0087B4] p-12 text-white"
              data-testid="ict-block"
            >
              <Server className="w-12 h-12 mb-6" />
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl mb-4">
                Enterprise ICT Professional Services
              </h2>
              <p className="font-inter text-lg text-white/90 leading-relaxed">
                Technology Transformation - Designing, integrating and governing 
                the enterprise ICT ecosystem.
              </p>
            </motion.div>

            {/* Leadership Block */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#4DB848] p-12 text-white"
              data-testid="leadership-block"
            >
              <Users className="w-12 h-12 mb-6" />
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl mb-4">
                Leadership Capability Services
              </h2>
              <p className="font-inter text-lg text-white/90 leading-relaxed">
                Leadership Transformation - Developing leaders for 
                transformation and sustainable growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise ICT Section */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="ict-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Section 1
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Enterprise ICT Architecture & Professional Services
            </h2>
            <p className="body-text max-w-4xl">
              SGA STRAT delivers a strategy-led professional services layer that enables 
              organisations to design, deploy, secure, and continuously optimise their 
              enterprise ICT environment. Acting as a technology orchestration partner, 
              SGA aligns cloud infrastructure, enterprise networks, cybersecurity frameworks, 
              and unified communications platforms into a unified operating model.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ictCapabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                data-testid={`ict-capability-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-100 p-6 border-l-4 border-l-[#4DB848] hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-[#4DB848]/10 flex items-center justify-center mb-4">
                  <capability.icon className="w-6 h-6 text-[#4DB848]" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg text-[#0B1E2D] mb-3">
                  {capability.title}
                </h3>
                <p className="font-inter text-sm text-slate-600 mb-4">
                  {capability.description}
                </p>
                <ul className="space-y-2">
                  {capability.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <BrandDot size="xs" />
                      <span className="font-inter text-xs text-slate-500">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Integration Advantage */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 bg-[#0B1E2D] p-8 md:p-12"
          >
            <h3 className="font-montserrat font-bold text-2xl text-white mb-4">
              The SGA Integration Advantage
            </h3>
            <p className="font-inter text-slate-300 leading-relaxed mb-6">
              Unlike traditional system integrators, SGA STRAT orchestrates the entire ICT 
              ecosystem by integrating critical technology layers into a cohesive operating 
              environment.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Cloud Platforms */}
              <a 
                href="https://ishantechnologies.com/data-center-services"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/10 p-5 hover:bg-white/15 transition-colors"
              >
                <h4 className="font-montserrat font-semibold text-white mb-2 group-hover:text-[#4DB848] transition-colors">
                  Cloud Platforms
                </h4>
                <p className="font-inter text-xs text-slate-300 leading-relaxed">
                  Direct connectivity to AWS, Google, Microsoft & Oracle via carrier-based cloud interconnect (CBCI) for secure, efficient data transfer.
                </p>
              </a>

              {/* Network Infrastructure */}
              <a 
                href="https://ishantechnologies.com/network-services"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/10 p-5 hover:bg-white/15 transition-colors"
              >
                <h4 className="font-montserrat font-semibold text-white mb-2 group-hover:text-[#4DB848] transition-colors">
                  Network Infrastructure
                </h4>
                <p className="font-inter text-xs text-slate-300 leading-relaxed">
                  Enterprise connectivity with High-Availability ILL, Dedicated Internet Access (DIA), and Clean Pipe services for robust performance.
                </p>
              </a>

              {/* Cybersecurity Frameworks */}
              <a 
                href="https://ishantechnologies.com/cybersecurity-services"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/10 p-5 hover:bg-white/15 transition-colors"
              >
                <h4 className="font-montserrat font-semibold text-white mb-2 group-hover:text-[#4DB848] transition-colors">
                  Cybersecurity Frameworks
                </h4>
                <p className="font-inter text-xs text-slate-300 leading-relaxed">
                  Modern threat defence with VAPT, Managed SASE, XDR, MDR, SIEM, and comprehensive security solutions.
                </p>
                <span className="inline-block mt-2 text-xs text-[#4DB848] hover:underline">
                  View Case Studies →
                </span>
              </a>

              {/* Voice & Collaboration */}
              <a 
                href="https://ishantechnologies.com/network-services/unified-communication"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/10 p-5 hover:bg-white/15 transition-colors"
              >
                <h4 className="font-montserrat font-semibold text-white mb-2 group-hover:text-[#4DB848] transition-colors">
                  Voice & Collaboration
                </h4>
                <p className="font-inter text-xs text-slate-300 leading-relaxed">
                  Unified Communications with reliability, scalability, security, and cost-effective collaboration solutions.
                </p>
              </a>

              {/* Secure Voice */}
              <a 
                href="https://assertion.cloud/"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/10 p-5 hover:bg-white/15 transition-colors"
              >
                <h4 className="font-montserrat font-semibold text-white mb-2 group-hover:text-[#4DB848] transition-colors">
                  Secure Voice
                </h4>
                <p className="font-inter text-xs text-slate-300 leading-relaxed">
                  Enterprise-grade trusted caller ID, contact center voice security, and real-time voice application observability.
                </p>
              </a>

              {/* Vendor Ecosystems */}
              <div className="bg-white/10 p-5">
                <h4 className="font-montserrat font-semibold text-white mb-2">
                  Vendor Ecosystems
                </h4>
                <p className="font-inter text-xs text-slate-300 leading-relaxed">
                  Strategic partnerships with Ishan Technologies, Zoom, Success KPI, Sify DC, and TTBS for unified ecosystem management.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-24 bg-white" data-testid="leadership-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#4DB848] mb-4 block">
              Section 2
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Leadership Capability Services
            </h2>
            <p className="body-text max-w-4xl">
              Successful transformation requires capable leadership and empowered teams. 
              SGA STRAT develops leadership capabilities that enable organizations to 
              execute strategy effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {leadershipCapabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                data-testid={`leadership-capability-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-slate-50 p-8 border-l-4 border-[#4DB848] hover:bg-white hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4DB848]/10 flex items-center justify-center flex-shrink-0">
                    <capability.icon className="w-6 h-6 text-[#4DB848]" />
                  </div>
                  <div>
                    <h3 className="font-montserrat font-semibold text-lg text-[#0B1E2D] mb-2">
                      {capability.title}
                    </h3>
                    <p className="font-inter text-sm text-slate-600 mb-4">
                      {capability.description}
                    </p>
                    <ul className="space-y-2">
                      {capability.items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <BrandDot size="xs" />
                          <span className="font-inter text-sm text-slate-500">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Training Videos Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 bg-slate-50 p-8 md:p-12"
            data-testid="training-videos-section"
          >
            <h3 className="font-montserrat font-bold text-xl text-[#0B1E2D] mb-4">
              Training & Workshop Videos
            </h3>
            <p className="font-inter text-slate-600 mb-8">
              Explore our leadership development programs, consultative selling workshops, 
              and strategy transformation sessions.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                'Leadership Development',
                'Consultative Selling',
                'Strategy Sessions',
              ].map((title, index) => (
                <div 
                  key={index}
                  className="aspect-video bg-slate-200 flex items-center justify-center border-2 border-dashed border-slate-300"
                >
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-[#0087B4] border-b-8 border-b-transparent ml-1" />
                    </div>
                    <span className="font-inter text-sm text-slate-500">{title}</span>
                    <p className="font-inter text-xs text-slate-400 mt-1">Video coming soon</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
